import { useState } from "react";
import { Spin, App } from "antd";
import { useOutletContext, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MenuOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useMonitors, useCreateMonitor } from "../../../../hooks/useMonitors";
import { useUsers } from "../../../../hooks/useUsers";
import { useStations } from "../../../../hooks/useStations";
import ProfileDropdown from "../../../../components/ProfileDropdown";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { logout, setAccess } from "../../../../store/authSlice";
import { denyAccess } from "../../../../api/access";
import type { ConfigOutletContext } from "../../Configuration";
import MonitorFormModal, { type MonitorFormValues } from "./MonitorFormModal";
import MonitorsTable from "./MonitorsTable";

const R = {
  bg: "var(--color-canvas)",
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  textSoft: "var(--color-body)",
  ink: "var(--color-ink)",
  brandTint: "var(--color-brand-tint)",
  danger: "var(--color-danger-ink)",
};

const MonitorsPage = () => {
  const { t } = useTranslation();
  const { isNarrow, onOpenMobileMenu } =
    useOutletContext<ConfigOutletContext>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const access = useAppSelector((s) => s.auth.access);
  const names = useAppSelector((s) => s.auth.names);
  const username = useAppSelector((s) => s.auth.username);
  const displayName = names || username || "";

  // Кой модал за създаване е отворен: за обикновен монитор или за шпикер.
  const [createVariant, setCreateVariant] = useState<
    null | "monitor" | "shpicker"
  >(null);
  const { message } = App.useApp();
  const createMonitorMutation = useCreateMonitor();

  const { data: monitors = [] } = useMonitors();
  const {
    data: users,
    isLoading: usersLoading,
    error: usersError,
  } = useUsers("monitors");
  const {
    data: stations,
    isLoading: stationsLoading,
    error: stationsError,
  } = useStations();

  const isLoading = usersLoading || stationsLoading;
  const hasInitialLoadError = usersError || stationsError;

  // За създаване се предлагат само СВОБОДНИ потребители от вида на секцията.
  const assignedUserIds = new Set(monitors.map((monitor) => monitor.userId));
  const availableUsers = (users ?? []).filter(
    (user) =>
      !assignedUserIds.has(user.id) &&
      !!user.shpicker === (createVariant === "shpicker"),
  );

  const handleCreate = async (values: MonitorFormValues) => {
    try {
      await createMonitorMutation.mutateAsync(values);
      message.success(t("monitorsConfig.saveSuccess"));
      setCreateVariant(null);
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      message.error(detail || t("monitorsConfig.saveError"));
      throw error;
    }
  };

  const handleLogout = async () => {
    if (access) {
        try {
          await denyAccess();
        } catch (e) {
          console.error("Deny access error:", e);
        }
        dispatch(setAccess(false));
        navigate("/", { replace: true });
      } else {
        await dispatch(logout());
        navigate("/login", { replace: true });
      }
  };

  return (
    <div className="h-full flex flex-col" style={{ background: R.bg }}>
      <style>{`
        @keyframes rspin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>

      {/* Top bar */}
      <div
        className="flex items-center justify-between flex-shrink-0"
        style={{
          padding: isNarrow ? "10px 16px" : "11px 28px",
          minHeight: isNarrow ? 48 : 60,
          borderBottom: `1px solid ${R.border}`,
          background: access
            ? `radial-gradient(ellipse 40% 120% at 100% 50%, ${R.brandTint} 0%, transparent 70%), ${R.surface}`
            : R.surface,
          gap: 10,
        }}
      >
        <div
          className="flex items-center"
          style={{ gap: 10, minWidth: 0, flex: 1 }}
        >
          {isNarrow && (
            <button
              onClick={onOpenMobileMenu}
              aria-label="Open menu"
              style={{
                height: 34,
                width: 34,
                borderRadius: 8,
                background: "transparent",
                border: "none",
                color: R.ink,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <MenuOutlined style={{ fontSize: 18 }} />
            </button>
          )}
          <h1
            style={{
              fontSize: isNarrow ? 18 : 22,
              fontWeight: 700,
              letterSpacing: -0.5,
              margin: 0,
              color: R.ink,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t("configSidebar.monitors")}
          </h1>
        </div>

        <div
          className="flex items-center"
          style={{ gap: isNarrow ? 6 : 10, flexShrink: 0 }}
        >
          <ProfileDropdown
            displayName={displayName}
            username={username ?? ""}
            access={access}
            isNarrow={isNarrow}
            onLogout={handleLogout}
          />
        </div>
      </div>

      {/* Body */}
      <div
        className="flex-1 min-h-0 overflow-y-auto"
        style={{ padding: isNarrow ? "14px 14px 24px" : "22px 28px 32px" }}
      >
        {/* Разстоянието между секциите = горния отстъп на страницата */}
        <div className="flex flex-col" style={{ gap: isNarrow ? 14 : 22 }}>
          {isLoading ? (
            <div
              style={{
                padding: 48,
                display: "flex",
                justifyContent: "center",
                background: R.surface,
                border: `1px solid ${R.border}`,
                borderRadius: 14,
              }}
            >
              <Spin size="large" />
            </div>
          ) : hasInitialLoadError ? (
            <div
              style={{
                padding: "22px 24px",
                background: R.surface,
                border: `1px solid ${R.border}`,
                borderRadius: 14,
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  background: "var(--color-danger-tint)",
                  color: R.danger,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ExclamationCircleOutlined style={{ fontSize: 16 }} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: R.ink,
                    letterSpacing: -0.2,
                  }}
                >
                  {t("monitorsConfig.loadingError")}
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: R.textSoft,
                    marginTop: 2,
                  }}
                >
                  {t("monitorsConfig.loadingErrorDesc")}
                </div>
              </div>
            </div>
          ) : users && stations ? (
            <>
              {/* Две отделни таблици — обикновени монитори и шпикери — за да
                  не се бъркат; всяка със свой бутон за добавяне. */}
              <MonitorsTable
                monitors={monitors.filter(
                  (monitor) =>
                    !users.find((user) => user.id === monitor.userId)?.shpicker,
                )}
                users={users}
                stations={stations}
                variant="monitor"
                isNarrow={isNarrow}
                onAddMonitor={() => setCreateVariant("monitor")}
              />
              <MonitorsTable
                monitors={monitors.filter(
                  (monitor) =>
                    !!users.find((user) => user.id === monitor.userId)?.shpicker,
                )}
                users={users}
                stations={stations}
                variant="shpicker"
                isNarrow={isNarrow}
                onAddMonitor={() => setCreateVariant("shpicker")}
              />

              {/* Общият модал за създаване (същият ползва и редакцията). */}
              <MonitorFormModal
                open={createVariant !== null}
                variant={createVariant ?? "monitor"}
                users={availableUsers}
                stations={stations}
                onSubmit={handleCreate}
                onCancel={() => setCreateVariant(null)}
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default MonitorsPage;
